import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';

import { glAuthAPI } from '../services/glApi';
import ValliLoginShowcase, { LOGIN_GREEN, LOGIN_INK, ValliMark } from './ValliLoginShowcase';

const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: 2.2,
    bgcolor: '#fff',
    '& fieldset': { borderColor: '#d5d9dc' },
    '&:hover fieldset': { borderColor: '#9aa5a0' },
    '&.Mui-focused fieldset': { borderColor: LOGIN_GREEN, borderWidth: 1.5 },
  },
};

export default function GLManagerLogin() {
  const navigate = useNavigate();
  const submitLockRef = useRef(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasToken, setHasToken] = useState(() => Boolean(localStorage.getItem('glToken')));

  const clearSession = () => {
    localStorage.removeItem('glToken');
    localStorage.removeItem('glUser');
    setHasToken(false);
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitLockRef.current || submitting) return;
    const cleanEmail = normalizeEmail(email);
    const cleanPassword = String(password || '');
    if (!cleanEmail || !cleanPassword) {
      setError('Enter your email address and password.');
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const response = await glAuthAPI.login({ email: cleanEmail, password: cleanPassword });
      const payload = response?.data?.data || response?.data || {};
      const token = payload.token || payload.glToken || response?.data?.token || '';
      const user = payload.user || payload.glUser || response?.data?.user || null;
      if (!token) throw new Error('Login succeeded but no token was returned.');
      localStorage.setItem('glToken', token);
      if (user) localStorage.setItem('glUser', JSON.stringify(user));
      else localStorage.removeItem('glUser');
      setHasToken(true);
      navigate('/gl-manager/overview', { replace: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Login failed. Please try again.');
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Box className="vf-login-page" component="main" sx={{ minHeight: '100dvh', minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(660px, 1.58fr) minmax(440px, 1fr)' }, bgcolor: '#fff', overflow: { xs: 'auto', lg: 'hidden' } }}>
      <ValliLoginShowcase />

      <Box sx={{ minHeight: '100dvh', minWidth: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 1.75, sm: 5, lg: 5.5, xl: 8 }, py: { xs: 2.5, sm: 4 }, ml: { lg: '-5vw' }, pl: { lg: '8vw', xl: '9vw' }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', minWidth: 0, maxWidth: 470 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, lg: 4.5 } }}>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, minWidth: 0 }}><ValliMark compact dark /></Box>
            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Tooltip title="Back to main login">
                <IconButton aria-label="Back to main login" onClick={() => navigate('/login')} sx={{ border: '1px solid #d8ddda', borderRadius: 2 }}>
                  <ArrowBackRoundedIcon />
                </IconButton>
              </Tooltip>
              <Button className="vf-login-language" aria-label="Language: English" startIcon={<LanguageRoundedIcon />} variant="outlined" size="small" sx={{ color: LOGIN_INK, borderColor: '#d8ddda', borderRadius: 2, textTransform: 'none' }}>
                English
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ display: { xs: 'none', lg: 'block' }, mb: 2.5 }}><ValliMark compact dark /></Box>
          <Typography sx={{ fontSize: { xs: 38, sm: 46 }, fontWeight: 950, letterSpacing: -1.8, color: LOGIN_INK, lineHeight: 1.05 }}>
            Welcome back
          </Typography>
          <Typography sx={{ mt: 1, color: '#46505a', fontSize: 16.5 }}>
            Sign in to access your Golden Lines Manager workspace
          </Typography>

          {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>{error}</Alert>}
          {hasToken && (
            <Alert severity="info" sx={{ mt: 2.5, borderRadius: 2 }}>
              A Golden Lines session is already saved on this browser.
              <Stack direction="row" spacing={1} sx={{ mt: 1.2 }}>
                <Button size="small" variant="contained" onClick={() => navigate('/gl-manager/overview', { replace: true })}>Continue</Button>
                <Button size="small" variant="outlined" startIcon={<LogoutRoundedIcon />} onClick={clearSession}>Clear session</Button>
              </Stack>
            </Alert>
          )}

          <Box component="form" onSubmit={submit} noValidate sx={{ mt: 3.3 }}>
            <Stack spacing={2.2}>
              <TextField
                required fullWidth label="Email address" type="email" value={email}
                onChange={(event) => setEmail(event.target.value)} disabled={submitting}
                autoComplete="email" sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: '#758078' }} /></InputAdornment> }}
              />
              <TextField
                required fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={password}
                onChange={(event) => setPassword(event.target.value)} disabled={submitting}
                autoComplete="current-password" sx={fieldSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: '#758078' }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                        <IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((current) => !current)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Typography sx={{ mt: 1, textAlign: 'right', color: '#627069', fontSize: 13.5 }}>
              Golden Lines access is created by an administrator.
            </Typography>

            <Button type="submit" fullWidth variant="contained" disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <LockOutlinedIcon />}
              sx={{ mt: 2.4, minHeight: 55, borderRadius: 2.1, bgcolor: LOGIN_GREEN, fontSize: 16, fontWeight: 900, '&:hover': { bgcolor: '#056f23' }, boxShadow: '0 12px 24px rgba(7,135,43,.22)' }}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Stack direction="row" alignItems="center" spacing={1.4} sx={{ my: 2.4 }}>
              <Divider sx={{ flex: 1 }} /><Typography color="text.secondary">or</Typography><Divider sx={{ flex: 1 }} />
            </Stack>

            <Button fullWidth type="button" variant="outlined" startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate('/login')}
              sx={{ minHeight: 54, borderRadius: 2.1, borderColor: LOGIN_GREEN, color: '#086d26', fontWeight: 900, fontSize: 15.5, '&:hover': { borderColor: '#056f23', bgcolor: '#f2faf4' } }}>
              Main portal login
            </Button>

            <Stack direction="row" spacing={1.3} sx={{ mt: 2.8, p: 1.6, borderRadius: 2.2, bgcolor: '#f3faf5', border: '1px solid #d5e9d9' }}>
              <SecurityOutlinedIcon sx={{ color: LOGIN_GREEN }} />
              <Box>
                <Typography sx={{ color: '#135d29', fontWeight: 850, fontSize: 14 }}>Separate secure manager access.</Typography>
                <Typography sx={{ color: '#557060', fontSize: 12.8, mt: 0.2 }}>Golden Lines permissions are independent from Site, Admin and Directors access.</Typography>
              </Box>
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="center" spacing={0.7} sx={{ mt: 3.5, color: '#8c9390' }}>
            <InsightsOutlinedIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontSize: 12.5 }}>© {new Date().getFullYear()} Valli Forecourts. All rights reserved.</Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
